/**
 * Katalog zboží v horním menu — port z `old-js/modules/menuChange.js` (bez jQuery).
 * Vyžaduje Shoptet globals: `shoptet`, `getShoptetDataLayer`.
 */

const timers = {
  catalogTimerShow: /** @type {ReturnType<typeof setTimeout> | null} */ (null),
  catalogTimerHide: /** @type {ReturnType<typeof setTimeout> | null} */ (null),
  itemTimerShow: /** @type {ReturnType<typeof setTimeout> | null} */ (null),
  itemTimerHide: /** @type {ReturnType<typeof setTimeout> | null} */ (null),
};

/** @type {AbortController | null} */
let hoverListenersAbort = null;

function lsKey(cfg) {
  return typeof cfg.menuCatalog?.localStorageKey === 'string'
    ? cfg.menuCatalog.localStorageKey
    : 'ygCategoryPanelHTML';
}

function detectResolutionNav(bp) {
  const layout = window.shoptet?.layout;
  if (layout && typeof layout.detectResolution === 'function') {
    try {
      return layout.detectResolution(bp);
    } catch {
      // fall through
    }
  }
  const n = typeof bp === 'number' ? bp : Number(bp) || 992;
  return window.matchMedia(`(min-width: ${n}px)`).matches;
}

function shouldRunPanelAndHovers() {
  const mobileV = window.shoptet?.config?.mobileHeaderVersion;
  const bp = window.shoptet?.abilities?.config?.navigation_breakpoint ?? 992;
  return mobileV !== '1' || detectResolutionNav(bp);
}

function safePageType() {
  try {
    if (typeof getShoptetDataLayer === 'function') return getShoptetDataLayer()?.pageType;
  } catch {
    // ignore
  }
  return undefined;
}

/** Homepage v Shoptetu: `body.type-index` (+ DataLayer / legacy `in-index`). */
function isYgHomepageView() {
  if (typeof document === 'undefined' || !document.body) return false;
  return (
    document.body.classList.contains('type-index') ||
    document.body.classList.contains('in-index') ||
    safePageType() === 'homepage'
  );
}

function syncCatalogHomepageClass() {
  const li = document.querySelector('#navigation li.menu-item-catalog');
  if (!li) return;
  li.classList.toggle('homepage', isYgHomepageView());
}

function getHorizontalNav() {
  return document.querySelector('nav#navigation');
}

/** Na homepage je výpis kategorií ve sloupci (`.side-catalog-wrap`), na podstránkách v rozbalovači v `#navigation`. */
function catalogNavigationMarkupPresent() {
  return Boolean(
    document.querySelector(
      '#navigation li.menu-item-catalog .yg-goods-catalog.nav-catalog-wrap .navigation-in',
    ),
  );
}

function catalogSidebarMarkupPresent() {
  return Boolean(
    document.querySelector('.sidebar.sidebar-left .yg-goods-catalog.side-catalog-wrap .navigation-in'),
  );
}

/**
 * Hlavní řádek horního menu (stejná větev DOM jako u selektorů na položky kategorií).
 * Obecný `ul.menu-level-1` může v šabloně existovat i jinde (mobile helper, duplicitní výpis) —
 * katalog musí být první položkou tohoto konkrétního seznamu.
 */
function getMainHorizontalMenuLevel1Ul() {
  const nav = getHorizontalNav();
  if (!nav) return null;
  const candidates = [
    nav.querySelector('ul.menu-level-1[data-testid="headerMenuItems"]'),
    nav.querySelector('ul.menu-level-1[role="menubar"]'),
    nav.querySelector(':scope > .navigation-in.menu > ul.menu-level-1'),
    nav.querySelector('.navigation-in.menu > ul.menu-level-1'),
    nav.querySelector(':scope > .navigation-in > ul.menu-level-1'),
    nav.querySelector('ul.menu-level-1'),
  ];
  for (const el of candidates) {
    if (el && nav.contains(el)) return el;
  }
  return null;
}

function getCategoryPanel() {
  return document.getElementById('categories');
}

function anchorMainCategoryMeta(anchor) {
  const b = anchor.querySelector(':scope > b');
  const title = (b?.textContent || anchor.textContent || '').trim();
  const href = (anchor.getAttribute('href') || '').trim();
  return { title, href };
}

function findCategoryInLeftCategoryPanelByTitleURL(title, url) {
  const topics = document.querySelectorAll('#categories .categories > .topic > a');
  for (const a of topics) {
    const meta = anchorMainCategoryMeta(a);
    if (meta.title === title && meta.href === url) return true;
  }
  return false;
}

function findCategoryInTopNavByTitleURL(title, url) {
  const items = document.querySelectorAll(
    '#navigation > .navigation-in.menu > ul.menu-level-1 > li:not(.menu-item-catalog)',
  );
  for (const li of items) {
    const a = li.querySelector(':scope > a');
    if (!a) continue;
    const t = (a.querySelector(':scope > b')?.textContent || a.textContent || '').trim();
    const h = (a.getAttribute('href') || '').trim();
    if (t === title && h === url) return li;
  }
  return null;
}

/** Duplicitní položky v „menu helper“ (cropped horní menu). */
function findHelperNavLiByTitleURL(title, url) {
  const items = document.querySelectorAll('.menu-helper ul.menu-level-1 > li:not(.menu-item-catalog)');
  for (const li of items) {
    const a = li.querySelector(':scope > a');
    if (!a) continue;
    const t = (a.querySelector(':scope > b')?.textContent || a.textContent || '').trim();
    const h = (a.getAttribute('href') || '').trim();
    if (t === title && h === url) return li;
  }
  return null;
}

/**
 * Odstranění / skrytí řádků v horním menu, které už jsou v rozbalovacím katalogu.
 * Spoleh na title+href (stejně jako původní deduplikace v LS větvi), ne jen na CSS třídy řádku.
 * @param {0|1} deleteMode 0 = odebrat z DOM; 1 = přidat .yg-menu-topnav-item-delete (CSS ze šablony)
 */
function applyDuplicateTopNavAction(catTitle, catUrl, deleteMode) {
  const main = findCategoryInTopNavByTitleURL(catTitle, catUrl);
  const helper = findHelperNavLiByTitleURL(catTitle, catUrl);
  if (deleteMode === 0) {
    main?.remove();
    helper?.remove();
  } else {
    main?.classList.add('yg-menu-topnav-item-delete');
    helper?.classList.add('yg-menu-topnav-item-delete');
  }
}

/** @returns {0|1} */
function duplicateTopNavDeleteMode(cfg) {
  return cfg.menuCatalog?.duplicateTopNavStrategy === 'hideClass' ? 1 : 0;
}

function callSplitMenu() {
  try {
    window.shoptet?.menu?.splitMenu?.();
  } catch {
    // ignore
  }
}

/**
 * @param {Record<string, unknown>} cfg
 * @param {boolean} removeFoundTopNavItems
 * @param {number} deleteMode 0 = remove nodes; 1 = add yg-menu-topnav-item-delete
 * @param {{ skipLeftPanelFilter?: boolean }} [opts]
 */
function getCategoryPanelHTML(cfg, removeFoundTopNavItems, deleteMode, opts = {}) {
  const skipLeft = opts.skipLeftPanelFilter === true;

  const nav = getHorizontalNav();
  if (!nav) return '';

  const placeholderId = `yg_category_panel_${Date.now()}`;
  const placeholder = document.createElement('div');
  placeholder.id = placeholderId;
  placeholder.style.display = 'none';
  placeholder.innerHTML = nav.innerHTML;
  document.body.appendChild(placeholder);

  placeholder.querySelectorAll('li.menu-item-catalog').forEach(el => el.remove());
  placeholder.querySelectorAll('ul.menu-level-1 li').forEach(li => li.classList.remove('splitted'));

  const mainCategories = placeholder.querySelectorAll('ul.menu-level-1 > li:not(.menu-item-catalog)');

  mainCategories.forEach(element => {
    const a = element.querySelector(':scope > a');
    if (!a) return;
    const cat_t = (a.querySelector(':scope > b')?.textContent || a.textContent || '').trim();
    const cat_url = (a.getAttribute('href') || '').trim();

    if (!skipLeft && !findCategoryInLeftCategoryPanelByTitleURL(cat_t, cat_url)) {
      element.remove();
      return;
    }

    if (removeFoundTopNavItems) {
      applyDuplicateTopNavAction(cat_t, cat_url, deleteMode);
    }
  });

  callSplitMenu();

  const html = placeholder.innerHTML;
  placeholder.remove();

  try {
    localStorage.setItem(lsKey(cfg), html);
  } catch {
    // quota / private mode
  }

  return html;
}

function delTopNavItByLSPan() {
  const generated = document.querySelectorAll('#navigation .menu-level-1 .menu-item-catalog .menu-level-1 > li');

  generated.forEach(element => {
    const a = element.querySelector(':scope > a');
    if (!a) return;
    const cat_t = (a.querySelector(':scope > b')?.textContent || a.textContent || '').trim();
    const cat_url = (a.getAttribute('href') || '').trim();
    const found = findCategoryInTopNavByTitleURL(cat_t, cat_url);
    if (found) found.remove();
  });

  callSplitMenu();
}

function addHorizontalNavCatalogBtn(cfg) {
  const ul = getMainHorizontalMenuLevel1Ul();
  if (!ul || ul.querySelector(':scope > li.menu-item-catalog')) return;

  const label =
    typeof cfg.menuCatalog?.catalogLabel === 'string' ? cfg.menuCatalog.catalogLabel : 'Katalog zboží';

  const li = document.createElement('li');
  li.className = 'menu-item-catalog';
  li.innerHTML = `<a>${escapeHtml(label)}</a><div class="yg-goods-catalog nav-catalog-wrap"></div>`;
  ul.insertBefore(li, ul.firstChild);
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getCategoryHTMLFromLS(cfg) {
  try {
    return localStorage.getItem(lsKey(cfg));
  } catch {
    return null;
  }
}

function ensureCatalogOverlay() {
  const existing = document.querySelectorAll('.yg-catalog-overlay');
  if (existing.length > 1) {
    for (let i = 1; i < existing.length; i++) existing[i].remove();
  }
  if (existing.length >= 1) return;

  const header = document.getElementById('header');
  const overlay = document.createElement('div');
  overlay.className = 'yg-catalog-overlay';
  if (header?.parentNode) header.parentNode.insertBefore(overlay, header.nextSibling);
  else document.body.appendChild(overlay);
}

function getCatalogMouseElement() {
  const navCatalog =
    document.querySelector('#navigation li.menu-item-catalog .yg-goods-catalog.nav-catalog-wrap') ||
    document.querySelector('#navigation li.menu-item-catalog .yg-goods-catalog');
  if (navCatalog) return navCatalog;
  if (document.body.classList.contains('in-index')) {
    return document.querySelector('body.in-index .yg-goods-catalog');
  }
  return document.querySelector('#navigation li.menu-item-catalog .yg-goods-catalog');
}

function getCatalogNavLi() {
  return document.querySelector('#navigation li.menu-item-catalog');
}

function showSubmenu(el) {
  el.classList.add('exp');
}

/**
 * Pouze řádek 1. úrovně v `.navigation-in` (nie položky uvnitř megapanelu `ul.menu-level-2`).
 * Globálně `.menu-level-1 > li` by jinak mátlo strukturu Shoptetu.
 */
function hideSubmenu() {
  document.querySelectorAll('.yg-goods-catalog').forEach(wrap => {
    const navIn = wrap.querySelector('.navigation-in');
    const ul1 =
      navIn?.querySelector(':scope > ul.menu-level-1') || navIn?.querySelector('ul.menu-level-1');
    if (!ul1?.classList.contains('menu-level-1')) return;
    ul1.querySelectorAll(':scope > li.exp').forEach(li => li.classList.remove('exp'));
  });
}

/**
 * řádek 1. stupně rozbalového katalogu (čísla v levém sloupci), ne odkaz ani li uvnitř pravého mega panelu.
 */
function getCatalogLeftColumnLevel1Li(ev) {
  const target = /** @type {HTMLElement | null} */ (ev.target);
  if (!(target instanceof Element)) return null;
  const goods = target.closest('.yg-goods-catalog');
  if (!goods) return null;

  const li = /** @type {HTMLElement | null} */ (target.closest('li'));
  if (!li || li.closest('ul.menu-level-2')) return null;

  const ul = li.parentElement;
  if (!ul?.classList.contains('menu-level-1')) return null;

  const navIn = goods.querySelector('.navigation-in');
  if (!navIn || !navIn.contains(li)) return null;

  return li;
}

/**
 * Třída `opened` musí být na `li.menu-item-catalog` — CSS ji tak očekává (viz `_menu-catalog.less`).
 * Dřív se omylem přidávala na vnitřní `.yg-goods-catalog`, takže dropdown zůstal `visibility:hidden`.
 */
function openSubpageCatalog(el) {
  const li =
    (el instanceof Element && el.closest('li.menu-item-catalog')) || getCatalogNavLi();
  li?.classList.add('opened');
}

function hideSubpageCatalog(el) {
  const li =
    (el instanceof Element && el.closest('li.menu-item-catalog')) || getCatalogNavLi();
  li?.classList.remove('opened');
}

function showCatalogOverlay() {
  document.body.classList.add('yg-show-catalog-overlay');
}

function hideCatalogOverlay() {
  document.body.classList.remove('yg-show-catalog-overlay');
}

/**
 * @param {Record<string, unknown>} cfg
 * @param {(cfg: Record<string, unknown>, ...args: unknown[]) => void} dbg
 */
export function changeCategoryPanel(cfg, dbg) {
  const panel = getCategoryPanel();

  if (panel) {
    addHorizontalNavCatalogBtn(cfg);

    const categoryPanelHTML = getCategoryPanelHTML(cfg, true, duplicateTopNavDeleteMode(cfg));
    const isHome = isYgHomepageView();
    const catalogLi = document.querySelector('#navigation .menu-item-catalog');
    const sidebar = document.querySelector('.sidebar.sidebar-left');

    /**
     * Stejná logika jako `old-js/menuChange.js`:
     * – homepage: HTML z kopie horního menu (filtrované podle #categories) do levého sloupce `side-catalog-wrap`;
     *   rozbalovač v navigaci se neplní — třída `homepage` vypne hover dropdown u tlačítka.
     * – jinak: obsah jen v `.nav-catalog-wrap`, sidebar bez našeho katalogu; duplicitní položky mažeme jen z horního menu + menu-helper.
     */
    if (isHome && sidebar) {
      if (categoryPanelHTML.trim() && !catalogSidebarMarkupPresent()) {
        document.querySelector('.sidebar.sidebar-left > .yg-goods-catalog.side-catalog-wrap')?.remove();
        sidebar.insertAdjacentHTML(
          'afterbegin',
          `<div class="yg-goods-catalog side-catalog-wrap">${categoryPanelHTML}</div>`,
        );
      }
      catalogLi?.classList.add('homepage');
    } else {
      document.querySelector('.sidebar.sidebar-left > .yg-goods-catalog.side-catalog-wrap')?.remove();
      const targetPanel = document.querySelector('#navigation .yg-goods-catalog.nav-catalog-wrap');
      if (targetPanel && categoryPanelHTML.trim() && !targetPanel.querySelector('.navigation-in')) {
        targetPanel.insertAdjacentHTML('afterbegin', categoryPanelHTML);
      }
      catalogLi?.classList.remove('homepage');
    }
  } else {
    /**
     * Bez levého #categories (některé náhledy / AJAX stavy) nemáme LS ani při prvním načtení —
     * stará větev bez ní nevložila ani tlačítko. Vygenerujeme výpis z horního menu bez párování na #categories.
     */
    addHorizontalNavCatalogBtn(cfg);
    let html = getCategoryHTMLFromLS(cfg);
    if (html === null || html.trim() === '') {
      html = getCategoryPanelHTML(cfg, true, duplicateTopNavDeleteMode(cfg), {
        skipLeftPanelFilter: true,
      });
    }
    const wrap = document.querySelector('#navigation .yg-goods-catalog.nav-catalog-wrap');
    if (wrap && html && html.trim() && !wrap.querySelector('.navigation-in')) {
      wrap.insertAdjacentHTML('afterbegin', html);
    }
    delTopNavItByLSPan();
  }

  dbg(cfg, 'menuCatalog: changeCategoryPanel done');
}

function finalizeCatalogCollapse() {
  clearTimeout(timers.catalogTimerHide);
  hideSubmenu();
  const mouseEl = getCatalogMouseElement();
  if (mouseEl) hideSubpageCatalog(mouseEl);
  hideCatalogOverlay();
  clearTimeout(timers.catalogTimerShow);
}

/** Katalog rozbalený rozumně vůči mikroskokům mimo výřez (grace ~ jako původní `mouseleave` 500 ms v menuChange.js). */
export function changeCatalogHovers(cfg, dbg) {
  hoverListenersAbort?.abort();
  clearTimeout(timers.catalogTimerShow);
  clearTimeout(timers.catalogTimerHide);
  clearTimeout(timers.itemTimerShow);
  clearTimeout(timers.itemTimerHide);

  hoverListenersAbort = new AbortController();
  const { signal } = hoverListenersAbort;

  ensureCatalogOverlay();

  const bp = window.shoptet?.abilities?.config?.navigation_breakpoint ?? 992;
  const mobileV = window.shoptet?.config?.mobileHeaderVersion;

  const navCatalogRoot = document.querySelector('#navigation li.menu-item-catalog:not(.homepage)');

  if (navCatalogRoot && !isYgHomepageView()) {
    /** Každý pohyb nad katalogem maže jen odložené „zabalit“ — neotvírám znova, už je-li otevřené (bez blikání). */
    navCatalogRoot.addEventListener(
      'mouseover',
      () => {
        if (!(mobileV !== '1' || detectResolutionNav(bp))) return;
        clearTimeout(timers.catalogTimerHide);

        const alreadyOpen =
          navCatalogRoot.classList.contains('opened') ||
          document.body.classList.contains('yg-show-catalog-overlay');

        clearTimeout(timers.catalogTimerShow);
        if (alreadyOpen) return;

        timers.catalogTimerShow = setTimeout(() => {
          clearTimeout(timers.catalogTimerHide);
          const mouseEl = getCatalogMouseElement();
          if (mouseEl) {
            openSubpageCatalog(mouseEl);
            showCatalogOverlay();
          }
        }, 100);
      },
      { signal, capture: true },
    );

    navCatalogRoot.addEventListener(
      'mouseleave',
      e => {
        if (!(mobileV !== '1' || detectResolutionNav(bp))) return;
        const to = e.relatedTarget;
        if (to instanceof Node && navCatalogRoot.contains(to)) return;
        clearTimeout(timers.catalogTimerShow);
        clearTimeout(timers.catalogTimerHide);
        timers.catalogTimerHide = setTimeout(() => finalizeCatalogCollapse(), 500);
      },
      { signal },
    );
  }

  /** HP: jen sloupcový blok — mazat odložené submenu při pohybu uvnitř; zavřít řádek kategorií po opuštění rámu */
  const homeWrap = document.querySelector('.sidebar.sidebar-left .yg-goods-catalog.side-catalog-wrap');
  if (homeWrap && isYgHomepageView()) {
    homeWrap.addEventListener(
      'mouseover',
      () => {
        if (!(mobileV !== '1' || detectResolutionNav(bp))) return;
        clearTimeout(timers.catalogTimerHide);
      },
      { signal, capture: true },
    );
    homeWrap.addEventListener(
      'mouseleave',
      e => {
        if (!(mobileV !== '1' || detectResolutionNav(bp))) return;
        const to = e.relatedTarget;
        if (to instanceof Node && homeWrap.contains(to)) return;
        clearTimeout(timers.catalogTimerShow);
        clearTimeout(timers.catalogTimerHide);
        timers.catalogTimerHide = setTimeout(() => hideSubmenu(), 500);
      },
      { signal },
    );
  }

  /**
   * Rozbalení 1. úrovně – jen řádek v levém sloupci; pohyb v `ul.menu-level-2` nesměnit `exp` na jinou kategorii.
   */
  document.documentElement.addEventListener(
    'mouseover',
    e => {
      if (!(mobileV !== '1' || detectResolutionNav(bp))) return;

      const li = getCatalogLeftColumnLevel1Li(e);
      if (!li) return;

      clearTimeout(timers.itemTimerHide);
      clearTimeout(timers.catalogTimerHide);

      if (!li.classList.contains('ext')) {
        clearTimeout(timers.itemTimerShow);
        hideSubmenu();
        return;
      }

      clearTimeout(timers.itemTimerShow);
      timers.itemTimerShow = setTimeout(() => {
        clearTimeout(timers.itemTimerHide);
        if (!li.classList.contains('exp')) {
          hideSubmenu();
          showSubmenu(li);
        }
      }, 150);
    },
    { signal, capture: true },
  );

  dbg(cfg, 'menuCatalog: changeCatalogHovers bound');
}

let menuLazyImagesBound = false;

/**
 * Načtení obrázků v rozbaleném menu bez zastaralého `$.fn.unveil()` (Shoptet deprecation).
 * Řeší typické data-src / data-original z lazy šablon.
 */
function bindMenuLazyImages(cfg, dbg) {
  if (menuLazyImagesBound) return;
  menuLazyImagesBound = true;
  document.documentElement.addEventListener(
    'mouseover',
    e => {
      const ext = /** @type {HTMLElement} */ (e.target).closest?.('.menu-level-1 .ext');
      if (!ext) return;
      ext.querySelectorAll('img').forEach(img => {
        const el = /** @type {HTMLImageElement} */ (img);
        try {
          const ds = el.dataset;
          const lazyUrl = ds.src || ds.original || ds.lazySrc || ds.lazy;
          if (lazyUrl && el.getAttribute('src') !== lazyUrl) {
            el.src = lazyUrl;
          }
          el.loading = 'eager';
          if (typeof el.decode === 'function') el.decode().catch(() => {});
        } catch {
          // ignore
        }
      });
    },
    { capture: true },
  );
  dbg(cfg, 'menuCatalog: lazy menu images bound');
}

/**
 * @param {Record<string, unknown>} cfg
 * @param {(cfg: Record<string, unknown>, ...args: unknown[]) => void} dbg
 */
export function runMenuCatalog(cfg, dbg) {
  if (cfg.menuCatalog?.enabled !== true) return;

  const nav = getHorizontalNav();
  if (!nav) {
    dbg(cfg, 'menuCatalog: nav#navigation not found');
    return;
  }

  if (!shouldRunPanelAndHovers()) {
    dbg(cfg, 'menuCatalog: skipped (mobile header / breakpoint)');
    return;
  }

  bindMenuLazyImages(cfg, dbg);

  syncCatalogHomepageClass();

  const hasCatalogMarkup = catalogNavigationMarkupPresent() || catalogSidebarMarkupPresent();
  if (!nav.querySelector('li.menu-item-catalog') || !hasCatalogMarkup) {
    changeCategoryPanel(cfg, dbg);
    syncCatalogHomepageClass();
  }

  changeCatalogHovers(cfg, dbg);
}

/**
 * @param {Record<string, unknown>} cfg
 * @param {(cfg: Record<string, unknown>, ...args: unknown[]) => void} dbg
 */
export function installMenuCatalog(cfg, dbg) {
  const safeDbg =
    dbg ||
    ((c, ...a) => {
      if (c?.debug === true) console.log('[YG menuCatalog]', ...a);
    });

  const tick = () => {
    try {
      runMenuCatalog(cfg, safeDbg);
    } catch (err) {
      safeDbg(cfg, 'menuCatalog error', err);
    }
  };

  tick();
  requestAnimationFrame(tick);
  setTimeout(tick, 350);
  document.addEventListener('ShoptetDOMContentLoaded', tick);
}
